import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { APP_CONFIG } from 'src/modules/main/providers/app.config';

import { AnalyticsAgent } from './analytics-agent';
import { CookieConsentService } from './cookie-consent/cookie-consent.service';
import { ITracker } from './cookie-consent/tracker.interface';
import { CookieCategory, TrackerID, UserConsentValue } from './cookie-consent/types';

/**
 * New relic documentation available at:
 *
 * @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/using-browser-apis/#agent-control-api
 * @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/using-browser-apis/#api-errors
 * @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/using-browser-apis/#api-traces
 * @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/using-browser-apis/#spa-api
 */
@Injectable({
    providedIn: 'root',
})
export class AnalyticsService implements OnDestroy, ITracker {
    public readonly id: TrackerID = 'nreum';
    public readonly category: CookieCategory = 'analytics';

    private readonly available: boolean;
    private readonly config: Record<string, unknown>;
    private readonly userConsentSubscription: Subscription | undefined;

    private agent: AnalyticsAgent | undefined;

    constructor(private cookieConsentService: CookieConsentService) {
        const appConfig = inject(APP_CONFIG);

        this.config = appConfig.ANALYTICS.options;
        this.available = appConfig.ANALYTICS.enabled;

        if (this.available) {
            this.agent = this.createAgent();
            this.userConsentSubscription = this.cookieConsentService.userConsent$
                .pipe(debounceTime(125))
                .subscribe(value => {
                    this.onUserConsent(value);
                });
        }
    }

    ngOnDestroy(): void {
        this.userConsentSubscription?.unsubscribe();
    }

    trackAction(action: string, attributes: Record<string, unknown>) {
        this.delegate(() => {
            console.warn('AnalyticsService.trackAction is not implemented', action, attributes);
        });
    }

    private createAgent(): AnalyticsAgent {
        this.agent = new AnalyticsAgent(this.config);
        return this.agent;
    }

    private delegate(fn: () => void): void {
        if (this.available && this.agent) {
            fn();
        }
    }

    private restart(): void {
        this.agent.collectNonEssentials();
    }

    private start(): void {
        this.createAgent();
        this.agent.collectNonEssentials();
    }

    private stop(): void {
        this.agent.collectOnlyEssentials();
    }

    private onUserConsent(current: UserConsentValue): void {
        if (!this.available) {
            return;
        }

        const activeCategory = current[this.category] === true;
        const activeService = current.services[this.category].includes(this.id);
        const shouldStart = activeCategory && activeService;

        if (shouldStart) {
            if (!this.agent) {
                return this.start();
            }

            return this.restart();
        }

        if (this.agent) {
            return this.stop();
        }
    }
}
